import { SupabaseJob } from '@applicator/shared'

import { formatRelevancePrompt, loadRelevanceSettings } from '../../lib/relevanceSettings'

import {
  loadJobDescription,
  updateJobRelevanceInDB,
  updateSupabaseJobInState
} from './handlers.relevance/data'
import {
  removeFromAnalyzing,
  getRelevanceSettings,
  getOpenAIConfig,
  isDescriptionValid,
  mapJobsWithUpdatedJob,
  createHeaders,
  markJobAsAnalyzing
} from './handlers.relevance/helpers'
import {
  createOpenAIThread,
  processOpenAIAnalysis
} from './handlers.relevance/openai'
import { parseRelevanceResult } from './handlers.relevance/parsing'
import { trackSuccessfulAnalysis, trackFailedAnalysis } from './handlers.relevance/tracking'
import { RelevanceHandlersParams } from './handlers.relevance/types'

export type { RelevanceHandlersParams }

// Helper function to validate OpenAI configuration
function validateOpenAIConfig(jobId: string, setAnalyzingJobIds: React.Dispatch<React.SetStateAction<Set<string>>>): { OPENAI_API_KEY: string; ASSISTANT_ID: string } | null {
  const config = getOpenAIConfig()
  if (!config.OPENAI_API_KEY || !config.ASSISTANT_ID) {
    alert('OpenAI API key or Assistant ID not configured. Please set VITE_OPENAI_API_KEY and VITE_OPENAI_ASSISTANT_ID in .env.local file.')
    removeFromAnalyzing(jobId, setAnalyzingJobIds)
    return null
  }
  return config
}

// Helper function to validate job description
function validateJobDescription(description: string, minLength: number, jobId: string, setAnalyzingJobIds: React.Dispatch<React.SetStateAction<Set<string>>>): boolean {
  if (!isDescriptionValid(description, minLength)) {
    alert(`Job description is too short or missing. Minimum length required: ${minLength} characters.`)
    removeFromAnalyzing(jobId, setAnalyzingJobIds)
    return false
  }
  return true
}

// Helper function to validate and prepare analysis
async function validateAndPrepareAnalysis(
  jobId: string,
  relevanceSettings: Awaited<ReturnType<typeof loadRelevanceSettings>> | null,
  setRelevanceSettings: React.Dispatch<React.SetStateAction<Awaited<ReturnType<typeof loadRelevanceSettings>> | null>>,
  supabaseJobs: SupabaseJob[],
  setSupabaseJobs: React.Dispatch<React.SetStateAction<SupabaseJob[]>>,
  setAnalyzingJobIds: React.Dispatch<React.SetStateAction<Set<string>>>
): Promise<{ settings: Awaited<ReturnType<typeof loadRelevanceSettings>>; description: string; headers: Record<string, string> } | null> {
  const settings = await getRelevanceSettings(relevanceSettings, setRelevanceSettings)
  if (!settings) {
    removeFromAnalyzing(jobId, setAnalyzingJobIds)
    return null
  }

  const config = validateOpenAIConfig(jobId, setAnalyzingJobIds)
  if (!config) {
    return null
  }

  const description = await loadJobDescription(jobId, supabaseJobs, setSupabaseJobs)
  if (!validateJobDescription(description, settings.minDescriptionLength, jobId, setAnalyzingJobIds)) {
    return null
  }

  const headers = createHeaders(config.OPENAI_API_KEY)
  return { settings, description, headers }
}

// Helper function to execute relevance analysis
async function executeRelevanceAnalysis(
  jobId: string,
  title: string,
  company: string,
  location: string,
  settings: Awaited<ReturnType<typeof loadRelevanceSettings>>,
  description: string,
  headers: Record<string, string>,
  ASSISTANT_ID: string
) {
  const threadId = await createOpenAIThread(headers)
  const truncatedDescription = description.substring(0, settings.maxDescriptionLength)
  const prompt = formatRelevancePrompt(
    settings.prompt,
    company,
    title,
    location,
    truncatedDescription
  )
  const lastMessage = await processOpenAIAnalysis(threadId, prompt, headers, ASSISTANT_ID)
  return parseRelevanceResult(lastMessage)
}

// Helper function to complete successful analysis
async function completeSuccessfulAnalysis(
  jobId: string,
  title: string,
  company: string,
  description: string,
  score: number,
  verdict: string | null,
  reasoning: string | null,
  isRelevant: boolean,
  analysisStartTime: number,
  setSupabaseJobs: React.Dispatch<React.SetStateAction<SupabaseJob[]>>
) {
  await updateJobRelevanceInDB(jobId, score, verdict, reasoning, isRelevant)
  updateSupabaseJobInState(jobId, score, verdict, reasoning, isRelevant, setSupabaseJobs, mapJobsWithUpdatedJob)
  const analysisLatency = Date.now() - analysisStartTime
  await trackSuccessfulAnalysis(jobId, title, company, description, score, verdict, reasoning, analysisLatency)
}

export const createRelevanceHandlers = ({
  supabaseJobs,
  setSupabaseJobs,
  setAnalyzingJobIds,
  _analyzingJobIds,
  relevanceSettings,
  setRelevanceSettings,
}: RelevanceHandlersParams) => {
  const handleAnalyzeRelevance = async (jobId: string, title: string, company: string, location: string) => {
    markJobAsAnalyzing(jobId, setAnalyzingJobIds)
    
    const analysisStartTime = Date.now()
    let description = ''
    
    try {
      const preparation = await validateAndPrepareAnalysis(
        jobId,
        relevanceSettings,
        setRelevanceSettings,
        supabaseJobs,
        setSupabaseJobs,
        setAnalyzingJobIds
      )
      if (!preparation) return

      const { settings, description: loadedDescription, headers } = preparation
      description = loadedDescription
      const { ASSISTANT_ID } = getOpenAIConfig()
      
      const { score, verdict, reasoning } = await executeRelevanceAnalysis(
        jobId,
        title,
        company,
        location,
        settings,
        description,
        headers,
        ASSISTANT_ID
      )
      
      const isRelevant = score >= (settings.scoreThreshold || 7)
      await completeSuccessfulAnalysis(
        jobId,
        title,
        company,
        description,
        score,
        verdict,
        reasoning,
        isRelevant,
        analysisStartTime,
        setSupabaseJobs
      )
      
    } catch (error: unknown) {
      const analysisLatency = Date.now() - analysisStartTime
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.error('Error analyzing job relevance:', errorMessage)
      alert(`Failed to analyze relevance: ${errorMessage}`)
      await trackFailedAnalysis(jobId, title, company, description, errorMessage, analysisLatency)
    } finally {
      removeFromAnalyzing(jobId, setAnalyzingJobIds)
    }
  }

  return {
    handleAnalyzeRelevance,
  }
}
